import {
  AdministrativeFlag,
  ApiResponse,
  AppealStatus,
  AppealType,
  ArClientType,
  ContactAttempt,
  ContactMethod,
  PaymentOutcome,
} from '@repo/types';

export type ArMatterResponse = ApiResponse<MatterItemDetail | undefined>;
export type ArMatterInvoicesResponse = ApiResponse<MatterInvoice[] | undefined>;
export type ArMatterInvoiceResponse = ApiResponse<MatterInvoice | undefined>;
export type ArMatterAttemptsResponse = ApiResponse<MatterAttempt[] | undefined>;
export type ArMatterInvoiceSummaryFilesResponse = ApiResponse<
  MatterInvoiceSummaryFile[] | undefined
>;
export type ArMatterInvoiceSummaryFileUploadResponse = ApiResponse<MatterInvoiceSummaryFile>;
export type ArAttemptAttachmentsResponse = ApiResponse<Attachment[] | undefined>;
export type ArRegisterAttemptAttachmentResponse = ApiResponse<Attachment[]>;
export type ArRemoveAttemptAttachmentResponse = ApiResponse<Attachment[]>;
export type ArAttachmentDownloadResponse = ApiResponse<{ url: string }>;
export type MatterArTabId = 'invoices' | 'attempts';
export interface Matter {
  clientId?: string;
  clientName?: string;
  matterId?: string;
  matterDisplayNumber: string;
  matterDescription?: string;
  originatingAttorneyId?: string;
  originatingAttorneyName?: string;
  responsibleAttorneyId?: string;
  responsibleAttorneyName?: string;
  outstandingFees?: number;
  outstandingExpenses?: number;
  totalOutstanding: number;
  maxDaysPastDue: number;
  numOfInvoices?: number;
  numOfMatters?: number;
}

export interface MatterItemDetail {
  matterId: string;
  matterNumber?: string;
  clientId?: string;
  matterDisplayNumber: string;
  matterDescription: string;
  clientName: string;
  totalOutstanding: number;
  outstandingFees: number;
  outstandingExpenses: number;
  maxDaysPastDue: number;
  lastContact?: string;
  nextFollowUp?: string;
  canSendEmail?: boolean;
  matterClientDeductible?: number;
  paidByInsured?: number;
  paidByCarrier?: number;
  totalOutstandingExpenses?: number;
  clientType: string;
  totalWipBalance: number;
  totalTrustBalance: number;
  numOfInvoices: number;
  isSIR?: boolean;
}

export interface AppealFile {
  filename: string;
  key: string;
  size?: number;
  contentType?: string;
  uploadAt?: string;
  uploadBy?: string;
  uploadByName?: string;
}

export interface MatterInvoice {
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  daysPastDue: number;
  total: number;
  outstandingBalance: number;
  appealStatus: AppealStatus;
  isCarrierInPreview: boolean;
  isSplitInvoice: boolean;
  collectedAmount?: number;
  remainingAmount?: number;
  appealComment?: string;
  originatingAttorneyId?: string;
  originatingAttorneyName?: string;
  responsibleAttorneyId?: string;
  responsibleAttorneyName?: string;
  matterDisplayNumber?: string;
  matterDescription?: string;
  emailQueueStatus?: string;
  matterId: string;
  invoiceNumbers?: string[];
  amountOutstanding: number;
  clientId: string;
  clientName: string;
  clientEmail: string;
  maxDaysPastDue: number;
  lastFollowUpDate?: string;
  clientType: string;
  appealId?: string;
  timekeepers?: Timekeeper[];
  paid?: number;
  creditsIssued?: number;
  writeOffAmount?: number;
  actualBalance?: number;
  arBalance?: number;
}
export interface ArMatter {
  matterId: string;
  clientId: string;
  clientName: string;
  clientType: ArClientType;
  matterNumber?: string;
  matterCustomNumber?: string;
  matterDisplayNumber: string;
  matterDescription: string;
  matterClientDeductible?: number;
  totalOutstanding?: number;
  totalOutstandingFees?: number;
  totalOutstandingExpenses?: number;
  numOfInvoices: number;
  paidByInsured?: number;
  paidByCarrier?: number;
}

export interface MatterInvoiceDetail extends MatterInvoice {
  clientName: string;
  matterDisplayNumber: string;
}

export interface MatterAttempt {
  sk: string;
  attemptDateTime: string;
  contactMethod: ContactMethod;
  notesGlobal?: string;
  nextFollowUp?: string;
  emailInfo?: AttemptEmailInfo;
  invoiceDetails: AttemptInvoice[];
  attemptedBy?: string;
  createdAt?: string;
}

export interface AttemptInvoice {
  invoiceId: string;
  invoiceNumber?: string;
  paymentOutcome?: PaymentOutcome;
  contactAttempt?: ContactAttempt;
  administrativeFlag?: AdministrativeFlag;
  shortNote?: string;
  extendedNote?: string;
}

export interface AttemptInvoiceDetail {
  id: string;
  invoiceIds?: string[];
  paymentOutcome?: PaymentOutcome;
  contactAttempt?: ContactAttempt;
  administrativeFlag?: AdministrativeFlag;
  shortNote?: string;
  extendedNote?: string;
}

export interface AttemptEmailInfo {
  emailId: string;
  subject: string;
  sentAt: string;
  senderUserId: string;
  toAddresses: { email: string }[];
  ccAddresses?: { email: string }[];
  emailBody: string;
}

export enum FileCategory {
  APPEALS_SUPPORT = 'Appeals Support',
  PAYMENT_SUPPORT = 'Payment Support',
  COLLECTION_ATTEMPT = 'Collection Attempt',
}
export interface MatterInvoiceSummaryFile {
  fileId: string;
  fileName: string;
  size: number;
  createdAt: string;
  createdBy: string;
  filePath: string;
  category: FileCategory;
}

export interface Attachment {
  key: string;
  fileName: string;
  fileSize: number;
  createdAt: string;
}

export interface RegisterAttemptAttachmentRequest {
  key: string;
  name: string;
  size: number;
  type: string;
  category: FileCategory;
}

export interface Timekeeper {
  id: string;
  email: string;
  name: string;
  initials?: string;
  isActive?: boolean;
  delegate?: string;
  delegateEmail?: string;
}

export interface AppealFormData {
  remainingAmount: number;
  comment: string;
  feesCut: number;
  expensesCut: number;
  promptPayment: number;
  bulkDiscount: number;
  administrativeFee: number;
  timekeepers?: Timekeeper[];
  deadline?: string;
  attyDeadline?: string;
  files?: MatterInvoiceSummaryFile[];
  includeCreditsIssued?: boolean;
  appealedFees?: number;
  appealedExpenses?: number;
}

export interface MarkToBeAppealedPayload {
  appealId?: string;
  appealType: AppealType;
  formData: AppealFormData;
}

export interface MarkDiscountOnlyPayload {
  promptPayment: number;
  bulkDiscount: number;
  administrativeFee: number;
  comment: string;
}

export interface MarkToBeAppealedResponse {
  invoiceId: string;
  remainingAmount: number;
  collectedAmount: number;
  comment: string;
  writeOffAmount: number;
  actualBalance: number;
}

export interface MarkWriteOffPayload {
  comment: string;
}

export interface MarkWriteOffResponse {
  invoiceId: string;
  comment: string;
  writeOffAmount: number;
  actualBalance: number;
}

export interface MarkDiscountOnlyResponse {
  invoiceId: string;
  remainingAmount: number;
  collectedAmount: number;
  comment: string;
  writeOffAmount: number;
  actualBalance: number;
}
